import React from 'react'
import { Link } from 'react-router-dom'
import { getImageUrl } from '../utils/api'

interface Project {
    _id: string; 
    name: string; 
    location: string; 
    address?: string;
    priceRange: string; 
    minPrice?: number;
    maxPrice?: number;
    status: string;
    projectType: string; 
    configuration: string; 
    images: string[];
    featuredImage?: string;
    coverImage?: string;
    gallery?: { exterior: string[] };
    overview?: { 
        priceRange?: string; 
        configuration?: string;
        projectArea?: string;
        floors?: string;
        totalTowers?: string;
        totalUnits?: string;
    };
    developerDetails?: { name: string; logo?: string };
    developer?: { name: string; logo?: string };
    rating?: number;
    createdAt?: string;
    slug?: string;
    projectStatus?: string;
}

export default function ListProject({ item, handleCompare }: { item: Project, handleCompare: (project: Project) => void }) {
    const projectImages = item.images && item.images.length > 0 ? item.images :
        (item.gallery?.exterior && item.gallery.exterior.length > 0 ? item.gallery.exterior :
            (item.featuredImage ? [item.featuredImage] : ['/assets/img/p-1.jpg']));

    const [isSaved, setIsSaved] = React.useState(() => {
        const saved = JSON.parse(localStorage.getItem('savedProjects') || '[]');
        return saved.includes(item._id);
    });

    const [isInCompare, setIsInCompare] = React.useState(() => {
        const stored = localStorage.getItem('compareList');
        if (stored) {
            try {
                const list = JSON.parse(stored);
                return list.some((p: any) => p._id === item._id);
            } catch (e) {
                return false;
            }
        }
        return false;
    });

    React.useEffect(() => {
        const handleCompareUpdate = () => {
            const stored = localStorage.getItem('compareList');
            if (stored) {
                try {
                    const list = JSON.parse(stored);
                    setIsInCompare(list.some((p: any) => p._id === item._id));
                } catch (e) {
                    setIsInCompare(false);
                }
            } else {
                setIsInCompare(false);
            }
        };

        window.addEventListener('compareUpdated', handleCompareUpdate);
        return () => window.removeEventListener('compareUpdated', handleCompareUpdate);
    }, [item._id]);

    const toggleSave = (e: React.MouseEvent) => {
        e.preventDefault();
        const saved = JSON.parse(localStorage.getItem('savedProjects') || '[]');
        let newSaved;
        if (isSaved) {
            newSaved = saved.filter((id: string) => id !== item._id);
        } else {
            newSaved = [...saved, item._id];
        }
        localStorage.setItem('savedProjects', JSON.stringify(newSaved));
        setIsSaved(!isSaved);
        window.dispatchEvent(new Event('storage'));
    };

    const developerName = item.developerDetails?.name || item.developer?.name || '';

    // Calculate total photos count
    const totalPhotos = projectImages.length;
    const projectSlugOrId = item.slug || item._id;

    return (
        <div className="bg-white rounded-xl border border-slate-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col sm:flex-row py-3 px-4 gap-6 group">
            {/* Left Column: Image Area */}
            <div className="relative w-full sm:w-[260px] md:w-[300px] h-[200px] sm:h-[180px] md:h-[210px] overflow-hidden rounded-xl flex-shrink-0 bg-slate-100">
                <Link to={`/project-details/${projectSlugOrId}`} className="block w-full h-full">
                    <img 
                        src={getImageUrl(projectImages[0])} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        alt={item.name} 
                    />
                </Link>
                
                {/* Verified Badge */}
                <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white rounded-md text-[9px] font-black uppercase tracking-wider shadow">
                    <i className="fa-solid fa-circle-check"></i>
                    Verified
                </div>

                {/* Media Counter Overlays */}
                {totalPhotos > 0 && (
                    <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white rounded-lg text-[10px] font-bold">
                        <i className="fa-solid fa-camera text-[10px]"></i>
                        <span>{totalPhotos} Photos</span>
                    </div>
                )}

                {/* Quick actions (compare, wishlist) overlay */}
                <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
                    <button 
                        onClick={() => handleCompare(item)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 ${
                            isInCompare 
                                ? 'bg-emerald-600 text-white' 
                                : 'bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        title={isInCompare ? "Added to Compare" : "Add to Compare"}
                    >
                        <i className={`fa-solid ${isInCompare ? 'fa-check' : 'fa-arrows-rotate'} text-xs`}></i>
                    </button>
                    <button 
                        onClick={toggleSave}
                        className="w-8 h-8 rounded-full bg-white text-slate-700 flex items-center justify-center shadow-lg transition-all hover:bg-red-50 hover:text-red-500 active:scale-90"
                        title={isSaved ? "Saved" : "Save Project"}
                    >
                        <i className={`fa-${isSaved ? 'solid' : 'regular'} fa-heart text-xs ${isSaved ? 'text-red-500' : ''}`}></i>
                    </button>
                </div>
            </div>

            {/* Right Column: Information Area */}
            <div className="flex-1 flex flex-col justify-between py-0">
                <div>
                    {/* Title and Badge Row */}
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-1.5">
                        <h3 className="text-xl md:text-2xl font-extrabold text-black leading-tight group-hover:text-blue-900 transition-colors">
                            <Link to={`/project-details/${projectSlugOrId}`}>
                                {item.name}
                            </Link>
                        </h3>
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#f0f4ff] text-blue-700 rounded-md text-[10px] font-extrabold uppercase">
                            {item.projectStatus || 'Newly Launched'}
                        </div>
                    </div>

                    {/* Developer Name */}
                    {developerName && (
                        <div className="mb-2">
                            <p className="text-[15px] font-bold text-black mb-0.5">
                                by <span className="text-black font-black">{developerName}</span>
                            </p>
                        </div>
                    )}

                    {/* Specifications Row */}
                    <div className="flex flex-wrap gap-x-3 gap-y-2 mb-2 text-[15px] font-black text-black">
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-bed text-black text-sm"></i>
                            <span>
                                {(() => {
                                    const config = item.overview?.configuration || item.configuration || '3';
                                    return config.toLowerCase().includes('bhk') ? config : `${config} BHK`;
                                })()}
                            </span>
                        </div>
                        <div className="text-black">|</div>
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-maximize text-black text-sm"></i>
                            <span>{item.overview?.projectArea || '5 Acres'}</span>
                        </div>
                        <div className="text-black">|</div>
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-building text-black text-sm"></i>
                            <span>{item.projectType || 'Residential'}</span>
                        </div>
                    </div>
                </div>

                {/* Footer Section: Pricing and CTAs */}
                <div className="pt-2 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 mt-auto">
                    {/* Price Range */}
                    <div>
                        <span className="text-[13px] font-extrabold text-black block uppercase tracking-wider mb-0.5">Price Range</span>
                        <span className="text-2xl font-black text-black">
                            {item.overview?.priceRange || item.priceRange || 'On Request'}
                        </span>
                    </div>

                    {/* Contacts & CTA */}
                    <div className="flex items-center justify-end w-full md:w-auto">
                        <Link 
                            to={`/project-details/${projectSlugOrId}`}
                            className="px-5 py-2.5 text-white rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 whitespace-nowrap hover:opacity-90"
                            style={{ background: 'linear-gradient(90deg, #05144b 0%, #4c1d95 100%)' }}
                        >
                            View Details
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}
